import React, { Component } from "react";
import clsx from "clsx";

import BoModal from "../common/BoModal";
import BackofficeService from "../../../services/BackofficeService";
import { I } from "../common/icons";
import { SERVICES, SERVICE_ORDER, FREQUENCIES, REPEAT_RULES, ACCOUNT_RULES, CADENCES, FIRST_SENDS, GROUP_PRESETS } from "../backofficeConstants";

const DEFAULT_FREQ = { payroll: "biweekly", monthend: "monthly", ap: "weekly", adhoc: "one_time" };
const DEFAULT_ANCHOR = { payroll: "period_start", monthend: "period_end", ap: "period_start", adhoc: "period_start" };

function blankTask(service) {
	return {
		key: Math.random().toString(36).slice(2),
		title: "",
		hint: "",
		description: "",
		groupName: "",
		offsetAnchor: DEFAULT_ANCHOR[service] || "period_start",
		startOffsetDays: 0,
		dueOffsetDays: 2,
		frequency: DEFAULT_FREQ[service] || "monthly",
		repeatRule: DEFAULT_ANCHOR[service] === "period_end" ? "relative_period_end" : "same_day",
		budget: { preparer: 1, reviewer: 0.25, manager: 0 },
		accountRule: "none",
		email: null,
		isClientLink: false,
		open: true,
	};
}

/**
 * Create or edit a backoffice template and its task rows.
 */
class TemplateEditorModal extends Component {
	constructor(props) {
		super(props);
		const t = props.template;
		this.state = {
			saving: false,
			error: null,
			name: t.name || "",
			service: t.service || "monthend",
			description: t.description || "",
			tasks: (t.tasks || []).map(x => Object.assign({ key: Math.random().toString(36).slice(2), open: false }, x, { budget: Object.assign({ preparer: 0, reviewer: 0, manager: 0 }, x.budget || {}), email: x.email ? Object.assign({}, x.email) : null })),
		};
	}

	set = (k, v) => this.setState({ [k]: v });

	setTask = (key, patch) => this.setState({ tasks: this.state.tasks.map(t => (t.key === key ? Object.assign({}, t, patch) : t)) });

	setTaskBudget = (key, role, value) => this.setState({ tasks: this.state.tasks.map(t => (t.key === key ? Object.assign({}, t, { budget: Object.assign({}, t.budget, { [role]: value }) }) : t)) });

	setTaskEmail = (key, patch) => this.setState({ tasks: this.state.tasks.map(t => (t.key === key ? Object.assign({}, t, { email: Object.assign({}, t.email || {}, patch) }) : t)) });

	addTask = () => this.setState({ tasks: this.state.tasks.concat([blankTask(this.state.service)]) });

	removeTask = key => this.setState({ tasks: this.state.tasks.filter(t => t.key !== key) });

	move = (key, dir) => {
		const tasks = this.state.tasks.slice();
		const i = tasks.findIndex(t => t.key === key);
		const j = i + dir;
		if (i < 0 || j < 0 || j >= tasks.length) return;
		[tasks[i], tasks[j]] = [tasks[j], tasks[i]];
		this.setState({ tasks });
	};

	save = async () => {
		const s = this.state;
		const name = s.name.trim();
		if (!name) return this.setState({ error: "Give the template a name." });
		const tasks = s.tasks
			.filter(t => String(t.title || "").trim())
			.map(t => ({
				title: t.title.trim(),
				hint: t.hint || null,
				description: t.description || null,
				groupName: t.groupName || null,
				offsetAnchor: t.offsetAnchor,
				startOffsetDays: parseInt(t.startOffsetDays, 10) || 0,
				dueOffsetDays: parseInt(t.dueOffsetDays, 10) || 0,
				frequency: t.frequency,
				repeatRule: t.repeatRule,
				budget: { preparer: parseFloat(t.budget.preparer) || 0, reviewer: parseFloat(t.budget.reviewer) || 0, manager: parseFloat(t.budget.manager) || 0 },
				accountRule: t.accountRule,
				email: t.email ? { subject: t.email.subject || "", body: t.email.body || "", firstSend: t.email.firstSend || "on_start", cadence: t.email.cadence || "every_3_days" } : null,
				isClientLink: !!t.isClientLink,
			}));
		this.setState({ saving: true, error: null });
		const payload = { name, service: s.service, description: s.description, tasks };
		const result = this.props.template.id ? await BackofficeService.updateTemplate(Object.assign({ templateId: this.props.template.id }, payload)) : await BackofficeService.createTemplate(payload);
		this.setState({ saving: false });
		if (!result.ok) return this.setState({ error: result.error });
		this.props.onSaved(result.data);
	};

	renderTask(t, idx) {
		return (
			<React.Fragment key={t.key}>
				<div className="bo-tpl-task-editor__row">
					<span className="bo-handle" title="Reorder">
						<button className="bo-ico" type="button" onClick={() => this.move(t.key, -1)} aria-label="Move up" style={{ padding: 0 }}>
							<I.chev style={{ transform: "rotate(180deg)" }} size={12} />
						</button>
					</span>
					<input type="text" value={t.title} onChange={e => this.setTask(t.key, { title: e.target.value })} placeholder="Task name" />
					<input type="number" value={t.startOffsetDays} onChange={e => this.setTask(t.key, { startOffsetDays: e.target.value })} aria-label="Start offset days" />
					<input type="number" value={t.dueOffsetDays} onChange={e => this.setTask(t.key, { dueOffsetDays: e.target.value })} aria-label="Due offset days" />
					<select value={t.frequency} onChange={e => this.setTask(t.key, { frequency: e.target.value })} aria-label="Repeats">
						{Object.keys(FREQUENCIES).map(k => (
							<option key={k} value={k}>
								{FREQUENCIES[k]}
							</option>
						))}
					</select>
					<input type="number" step="0.25" min="0" value={t.budget.preparer} onChange={e => this.setTaskBudget(t.key, "preparer", e.target.value)} aria-label="Preparer hours" />
					<input type="number" step="0.25" min="0" value={t.budget.reviewer} onChange={e => this.setTaskBudget(t.key, "reviewer", e.target.value)} aria-label="Reviewer hours" />
					<input type="number" step="0.25" min="0" value={t.budget.manager} onChange={e => this.setTaskBudget(t.key, "manager", e.target.value)} aria-label="Manager hours" />
					<span style={{ display: "flex", gap: 2 }}>
						<button className="bo-ico" type="button" onClick={() => this.setTask(t.key, { open: !t.open })} aria-label="More options" title="More options">
							<I.chev className={clsx("bo-chev", { "bo-chev--open": t.open })} />
						</button>
						<button className="bo-ico bo-ico--del" type="button" onClick={() => this.removeTask(t.key)} aria-label="Remove task">
							<I.trash />
						</button>
					</span>
					{t.open && (
						<div className="bo-tpl-task-editor__detail">
							<div className="bo-field">
								<label>Hint (shown in brackets)</label>
								<input type="text" value={t.hint || ""} onChange={e => this.setTask(t.key, { hint: e.target.value })} placeholder="e.g. Bank & credit card statement uploads" />
							</div>
							<div className="bo-field">
								<label>Group under</label>
								<input type="text" list={`groups-${t.key}`} value={t.groupName || ""} onChange={e => this.setTask(t.key, { groupName: e.target.value })} placeholder="None" />
								<datalist id={`groups-${t.key}`}>
									{GROUP_PRESETS.map(g => (
										<option key={g} value={g} />
									))}
								</datalist>
							</div>
							<div className="bo-field">
								<label>Offsets are from</label>
								<select value={t.offsetAnchor} onChange={e => this.setTask(t.key, { offsetAnchor: e.target.value, repeatRule: e.target.value === "period_end" ? "relative_period_end" : t.repeatRule === "relative_period_end" ? "same_day" : t.repeatRule })}>
									<option value="period_start">Period start</option>
									<option value="period_end">Period end</option>
								</select>
							</div>
							<div className="bo-field">
								<label>Repeat rule</label>
								<select value={t.repeatRule} onChange={e => this.setTask(t.key, { repeatRule: e.target.value })}>
									{Object.keys(REPEAT_RULES).map(k => (
										<option key={k} value={k}>
											{REPEAT_RULES[k]}
										</option>
									))}
								</select>
							</div>
							<div className="bo-field">
								<label>Tracked accounts</label>
								<select value={t.accountRule} onChange={e => this.setTask(t.key, { accountRule: e.target.value })}>
									{Object.keys(ACCOUNT_RULES).map(k => (
										<option key={k} value={k}>
											{ACCOUNT_RULES[k]}
										</option>
									))}
								</select>
							</div>
							<div className="bo-field">
								<label>Description</label>
								<textarea value={t.description || ""} onChange={e => this.setTask(t.key, { description: e.target.value })} />
							</div>
							<label className="bo-chk">
								<input type="checkbox" checked={!!t.isClientLink} onChange={e => this.setTask(t.key, { isClientLink: e.target.checked })} /> Client deliverable (shown as a link)
							</label>
							<label className="bo-chk">
								<input type="checkbox" checked={!!t.email} onChange={e => this.setTask(t.key, { email: e.target.checked ? { subject: `${t.title || "Task"} request`, body: "Hi {{contact_first_name}}, ", firstSend: "on_start", cadence: "every_3_days" } : null })} /> Client action required (email automation)
							</label>
							{t.email && (
								<>
									<div className="bo-field">
										<label>Email subject</label>
										<input type="text" value={t.email.subject || ""} onChange={e => this.setTaskEmail(t.key, { subject: e.target.value })} />
									</div>
									<div className="bo-field">
										<label>First send</label>
										<select value={t.email.firstSend || "on_start"} onChange={e => this.setTaskEmail(t.key, { firstSend: e.target.value })}>
											{Object.keys(FIRST_SENDS).map(k => (
												<option key={k} value={k}>
													{FIRST_SENDS[k]}
												</option>
											))}
										</select>
									</div>
									<div className="bo-field" style={{ gridColumn: "1 / -1" }}>
										<label>Email body</label>
										<textarea value={t.email.body || ""} onChange={e => this.setTaskEmail(t.key, { body: e.target.value })} />
									</div>
									<div className="bo-field">
										<label>Follow-up cadence</label>
										<select value={t.email.cadence || "every_3_days"} onChange={e => this.setTaskEmail(t.key, { cadence: e.target.value })}>
											{Object.keys(CADENCES).map(k => (
												<option key={k} value={k}>
													{CADENCES[k]}
												</option>
											))}
										</select>
									</div>
								</>
							)}
						</div>
					)}
				</div>
			</React.Fragment>
		);
	}

	render() {
		const { template, onClose } = this.props;
		const s = this.state;
		return (
			<BoModal
				title={template.id ? "Edit template" : "New backoffice template"}
				icon={<I.file />}
				wide
				onClose={onClose}
				footerLeft={
					<>
						<I.info />
						Offsets are days from the period anchor. Budgets are hours per occurrence.
					</>
				}
				footer={
					<>
						<button className="bo-btn" type="button" onClick={onClose}>
							Cancel
						</button>
						<button className="bo-btn bo-btn--primary ft-bo-template-save" type="button" onClick={this.save} disabled={s.saving}>
							{s.saving ? "Saving…" : "Save template"}
						</button>
					</>
				}
			>
				{s.error && <div className="bo-field__error">{s.error}</div>}
				<div className="bo-grid2">
					<div className="bo-field">
						<label>Template name</label>
						<input type="text" value={s.name} onChange={e => this.set("name", e.target.value)} placeholder="e.g. Month-end bookkeeping (standard)" autoFocus />
					</div>
					<div className="bo-field">
						<label>Service</label>
						<select value={s.service} onChange={e => this.set("service", e.target.value)}>
							{SERVICE_ORDER.map(k => (
								<option key={k} value={k}>
									{SERVICES[k].label}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="bo-field">
					<label>
						Description <small>(shown on the template card)</small>
					</label>
					<input type="text" value={s.description} onChange={e => this.set("description", e.target.value)} placeholder="e.g. 15 tasks · Statements & Queries emails · GEM self-review" />
				</div>
				<div className="bo-field">
					<label>
						Tasks <small>({s.tasks.length})</small>
					</label>
					<div className="bo-tpl-task-editor">
						<div className="bo-tpl-task-editor__row bo-tpl-task-editor__row--head">
							<span />
							<span>Task</span>
							<span title="Days from anchor">Start +d</span>
							<span title="Days from anchor">Due +d</span>
							<span>Repeats</span>
							<span>P h</span>
							<span>R h</span>
							<span>M h</span>
							<span />
						</div>
						{s.tasks.map((t, i) => this.renderTask(t, i))}
						{!s.tasks.length && <div className="bo-tpl-task-editor__row bo-muted">No tasks yet.</div>}
					</div>
					<button className="bo-addtask" type="button" onClick={this.addTask}>
						<I.plus />+ Add task to template
					</button>
				</div>
			</BoModal>
		);
	}
}

export default TemplateEditorModal;
